import React, { useImperativeHandle, useState } from "react";
import Panel from "components/panels/panel";
import { getCacheFriendlyInstruction } from "components/filters/Filters/InstructionHelpers";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { MySelectionTable } from "components/Tables";
import { FilterProvider, type FilterProviderRef, type FilterValues } from "components/filters/FilterContext";
import CardList from "components/Tables/CardList";
import FilterHeader from "components/filters/FilterHeader";
import ExportButton from "components/filters/Export/ExportButton";
import FilterPresetButton from "components/filters/preset/FilterPresetButton";
import { MySelectionProvider } from "components/Tables/MySelection/MySelectionContext";
import MySelectionButton from "components/Tables/MySelection/MySelectionButton";
import { hasSelectedItemCount, type AxiosPromiseWithRequest, type ListTemplateExtraFilter, type ListTemplateInstruction, type ListTemplateLayout, type ListTemplateOptions } from "./types";
import { useLayout } from "./useLayout";
import { appendFilterValuesToQueryString } from "components/filters/mappingUtils";
import { ButtonAccent } from "components/Atoms/Button";

export interface ListTemplateProps<
  TInstruction extends ListTemplateInstruction,
  TItem extends server.dto.IHasObjectPermissions,
  TFilter,
> {
  title: string
  filterSet: server.dto.FilterSets
  perPage?: number
  defaultFilters: TFilter
  initialInstruction: TInstruction
  fetchFunction: (instruction: TInstruction) => AxiosPromiseWithRequest<TItem, TFilter>
  getId: (item: TItem) => number
  layout: ListTemplateLayout<TItem>
  filters: JSX.Element
  renderExtraFilters?: (props: ListTemplateExtraFilter<TInstruction>) => JSX.Element
  functions: {
    mapFilterValuesToInstruction: (filterSet: server.dto.FilterSets, values: FilterValues, page: number, perPage: number) => TInstruction
    mapResponseToFilterValues: (response: TFilter, values: FilterValues) => Partial<FilterValues>
  }
  options?: ListTemplateOptions<TItem, TInstruction>
  onEdit?: (item: TItem) => void
  onDelete?: (item: TItem) => void
  onNew?: () => void
  isNewButtonLoading?: boolean
  articleId?: number
  listRef?: React.Ref<ListTemplateRef>
}

export interface ListTemplateRef {
  refetch: () => void;
}

export const listTemplateQueryKey = "listTemplateQueryKey";

const ListTemplate = <
  TInstruction extends ListTemplateInstruction,
  TItem extends server.dto.IHasObjectPermissions,
  TFilter,
>(props: ListTemplateProps<TInstruction, TItem, TFilter>) => {
  const {
    title,
    filterSet,
    perPage = 30,
    defaultFilters,
    initialInstruction,
    fetchFunction,
    getId,
    filters,
    renderExtraFilters,
    functions,
    options = {},
    onEdit,
    onNew,
    isNewButtonLoading
  } = props;
  const filterRef = React.useRef<FilterProviderRef>(null);
  const { layout, layoutButtons } = useLayout(props.layout);

  const initInstruction = (instruction: TInstruction) => ({
    ...instruction,
    page: 1,
    perPage,
    cache: filterSet
  });

  const [instruction, onInstructionChange] = useState<TInstruction>(initInstruction(initialInstruction));
  const [initialLoading, setInitialLoading] = useState(true);

  const api = useCustomQuery({
    queryFn: async () => {
      return fetchFunction(getCacheFriendlyInstruction(instruction));
    },
    queryKey: [listTemplateQueryKey, filterSet, instruction],
    onSuccess: (response) => {
      pagination.setPagination(response.data.pagination);
      if (initialLoading) {
        filterRef.current.setDefaultValues((prev: FilterValues) => {
          return functions.mapResponseToFilterValues(defaultFilters, prev);
        });
      }
      const filtersWithValues = filterRef.current.updateValues((prev: FilterValues) => {
        return functions.mapResponseToFilterValues(response.data.request, prev);
      });
      if (initialLoading) {
        appendFilterValuesToQueryString(filtersWithValues);
      }
      setInitialLoading(false);
    },
    cacheTime: 0
  });

  useImperativeHandle(props.listRef, () => ({
    refetch: () => api.refetch()
  }), [api]);

  const onFilterPresetApply = async (preset: server.dto.FilterPreset<TInstruction>) => {
    if (preset.filter == null) {
      filterRef.current.reset();
      return;
    }

    onInstructionChange(initInstruction(preset.filter));
  };

  const onFilterChange = (filter: FilterValues, filtersWithValues: Partial<FilterValues>) => {
    const newInstruction = functions.mapFilterValuesToInstruction(filterSet, filter, 1, perPage);
    appendFilterValuesToQueryString(filtersWithValues);
    onInstructionChange(newInstruction);
  };

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    onInstructionChange({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const onDelete = (item: TItem) => {
    window.displayDeleteConfirmationModal({
      onDeleteHandler: async () => {
        await props.onDelete(item);
        api.refetch();
      }
    })
  }

  const pagination = useManualPagination(instruction.page || 1, perPage, paginate);
  const exportButton = options.exportOptions && <ExportButton
    canExportList={options.exportOptions.canExportList}
    instruction={instruction}
    exportOptionsApi={options.exportOptions.exportOptionsApi}
  />;
  const isPaginationVisible = pagination.pageCount > 1;

  const displayFilterHeaderButtons = options.filterPreset || options.mySelection;

  const isTable = layout.type === "table";
  const isCard = layout.type === "card";

  let initialSelectionCount = 0;
  if (api.data != null && hasSelectedItemCount(api.data.data)) {
    initialSelectionCount = api.data.data.selectedCount;
  }

  return <Panel
    headerContainerClasses="top-navigation-wrapper"
    title={onNew ? title : undefined}
    button={onNew && <ButtonAccent label={RESX.Buttons.btnNew} onClick={onNew} loading={isNewButtonLoading} disabled={isNewButtonLoading} />}
    contentCssClass="flex-column panel-gap"
    headerSize={onNew ? "h1" : undefined}
    articleId={props.articleId}
  >
    <FilterProvider
      ref={filterRef}
      onFilterChange={onFilterChange}
    >
      <MySelectionProvider
        options={options.mySelection}
        initialSelectionCount={initialSelectionCount}
        items={api.data?.data.data ?? []}
        getId={getId}
        refetch={async () => api.refetch()}
      >
        <div className="flex panel-gap">

          <FilterHeader.Filters isLoading={initialLoading}>
            <div className="selectiontool-header-filters">
              {filters}
            </div>

            {displayFilterHeaderButtons && <FilterHeader.Buttons>
              {options.filterPreset && <FilterPresetButton
                cacheKey={options.filterPreset.cacheKey}
                filterSet={filterSet}
                instruction={instruction}
                onApplyPreset={onFilterPresetApply}
                fetchFunction={fetchFunction}
                mapResponseToFilterValues={functions.mapResponseToFilterValues}
                listApi={options.filterPreset.listApi}
                mutateApi={options.filterPreset.mutateApi}
              />}
              <MySelectionButton />
            </FilterHeader.Buttons>}
          </FilterHeader.Filters>
          {renderExtraFilters ? renderExtraFilters({ instruction, initialLoading }) : null}
        </div>

        <div className="s1_panel">
          {isTable &&
            <MySelectionTable
              items={api.data?.data.data ?? []}
              isLoading={api.isLoading}
              hideHeader={layout.hideHeader}
              columns={layout.columns}
              getKey={(i) => `${getId(i)}`}
              pagination={isPaginationVisible && pagination.Component}
              exportButton={exportButton}
              title={`${title}: ${pagination.total}`}
              onEdit={onEdit}
              canEdit={(i) => i.permissions.canModify}
              onDelete={props.onDelete ? onDelete : undefined}
              canDelete={(i) => i.permissions.canDelete}
              layoutButtons={layoutButtons}
            />}
          {isCard &&
            <CardList
              items={api.data?.data.data ?? []}
              isLoading={api.isLoading}
              sections={layout.sections}
              getId={getId}
              pagination={isPaginationVisible && pagination.Component}
              exportButton={exportButton}
              title={`${title}: ${pagination.total}`}
              onEdit={onEdit}
              canEdit={(i) => i.permissions.canModify}
              layoutButtons={layoutButtons}
            />
          }
        </div>
      </MySelectionProvider>
    </FilterProvider>
  </Panel>;
};

export default ListTemplate;